import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { DEMO_SESSION_TOKEN } from '@/lib/demo';
import type { RootState } from '@/store/store';
import type { GstAccount, GstinLookup, GstProfile } from '@/types/gst';
import { useDashboardQuery } from './useDashboardQuery';

/**
 * GST details and tax invoices (G1–G5). `null` means GST isn't live yet: the backend has no
 * endpoints for GSTIN lookup, saved GST details or invoices, so only the local demo session has data.
 */
export function useGstAccount() {
  const { data, ...rest } = useDashboardQuery<GstAccount | null>(
    async (demo) => {
      if (import.meta.env.DEV && demo) return (await import('@/pages/dev/demoData')).demoGstAccount();
      return null;
    },
    null,
    'We couldn’t load your GST details. Please try again.',
  );
  return { account: data, ...rest };
}

/** Looks a GSTIN up in GST records and saves the customer's GST details (demo session only for now). */
export function useGstActions() {
  const demo = useSelector((state: RootState) => state.auth.accessToken) === DEMO_SESSION_TOKEN;

  const lookup = useCallback(
    async (gstin: string): Promise<GstinLookup> => {
      if (import.meta.env.DEV && demo) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        return (await import('@/pages/dev/demoData')).demoLookupGstin(gstin);
      }
      throw new Error('GSTIN verification isn’t available yet.');
    },
    [demo],
  );

  const save = useCallback(
    async (profile: GstProfile) => {
      if (import.meta.env.DEV && demo) return (await import('@/pages/dev/demoData')).demoSaveGstProfile(profile);
      throw new Error('Saving GST details isn’t available yet.');
    },
    [demo],
  );

  return { lookup, save };
}
